<template>
  <PForm
    ref="pForm"
    :heading="formHeading"
    :primary-cta="submitButton"
    :secondary-cta="secondaryCta"
    :quaternary-cta="quaternaryCta"
    show-footer
    @close="onClose()"
    @submit.prevent="submitForm"
    @reset="resetForm"
  >
    <template v-if="formState.edit || !isAdvanceRequest" #header-info>
      <PBadge
        :label="useChangeCase(formData.status_cd ?? '', 'capitalCase').value"
        :type="formData.status_cd"
        class="q-mx-md"
      />
    </template>
    <div class="row q-col-gutter-lg">
      <div v-if="isTypeOfPaymentSelectorVisible" class="col-12">
        <PField
          v-model="formData.is_advance"
          label="Type of Payment"
          :disable="formState.view"
        >
          <template #control>
            <POptionGroup
              v-model="formData.is_advance"
              :disable="isPaymentsApprovalRoute"
              :options="[
                {
                  description: 'Request for payment for bill against a pod',
                  label: 'Vendor Payment',
                  value: false,
                },
                {
                  disable: true,
                  description: 'Request for advance payment amount',
                  label: 'Vendor Advance',
                  value: true,
                },
              ]"
            />
          </template>
        </PField>
      </div>

      <div v-if="isAdvanceRequest" class="col-12 q-mt-xs">
        <PSelectInput
          v-model.trim="formData.seller"
          :disable="disableVendorSelector"
          :emit-value="false"
          :map-options="false"
          :options="unref(sellerFilter.entities)"
          :option-label="getSellerOptionLabel"
          :options-loading="unref(sellerFilter.loading)"
          :validations="{
            required: true,
          }"
          label="Vendor"
          option-value="_id"
          placeholder="Select vendor"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          :cta="pastPaymentRequestsButton"
          @filter="sellerFilter.onFilter"
          @filter-abort="sellerFilter.onFilterAbort"
          @virtual-scroll="sellerFilter.onScroll"
          @update:model-value="populateVendorData"
        >
          <template #option="{ itemProps, opt }">
            <q-item v-bind="itemProps">
              <q-item-section>
                <q-item-label class="text-sm">
                  <span class="text-neutral">
                    {{ opt.shop_name }} {{ opt?.gstin ? `| ${opt.gstin}` : "" }}
                  </span>
                </q-item-label>
                <q-item-label v-if="opt.gstin || opt.state" caption lines="1">
                  <span class="text-neutral-70">
                    State: {{ opt.state }}
                    {{
                      opt?.tax_settings?.type === "tds"
                        ? `| TDS: ${opt.tax_settings.percentage}%`
                        : ""
                    }}
                  </span>
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </PSelectInput>
      </div>

      <div
        v-if="!isPaymentsApprovalRoute || (isAdvanceRequest && !formState.edit)"
        class="col-12 q-mt-none"
      >
        <PSelectInput
          v-model.trim="formData.approvers"
          :disable="disableApproverSelector"
          :options="unref(approversFilter.entities)"
          :options-loading="unref(approversFilter.loading)"
          :validations="{
            required: true,
          }"
          help="Add additional KAMs"
          label="Payment Approvers"
          option-label="name"
          placeholder="Select approvers"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          multiple
          use-chips
          @filter="approversFilter.onFilter"
          @filter-abort="approversFilter.onFilterAbort"
          @virtual-scroll="approversFilter.onScroll"
        >
          <template #option="{ itemProps, opt }">
            <q-item v-bind="itemProps" :disable="!opt.is_active">
              <q-item-section>
                <q-item-label class="text-sm">
                  <span class="text-neutral">{{ opt.name }}</span>
                </q-item-label>
                <q-item-label v-if="opt.email || opt.phone" caption lines="1">
                  <span class="text-neutral-70">
                    {{ opt.email }} {{ opt.email && opt.phone && "|" }}
                    {{ opt.phone }}
                  </span>
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </PSelectInput>
      </div>
    </div>
    <q-separator v-if="!isAdvanceRequest" class="q-mt-sm q-mb-xl" />
    <div class="row q-col-gutter-xxl">
      <div v-if="!isAdvanceRequest" class="col-12 q-mb-xs">
        <PSelectInput
          v-model.trim="formData.seller"
          :disable="Boolean(formState.view) || !isAdvanceRequest"
          :options="unref(sellerFilter.entities)"
          :option-label="getSellerOptionLabel"
          :options-loading="unref(sellerFilter.loading)"
          :validations="{
            required: true,
          }"
          label="Vendor"
          option-value="_id"
          placeholder="Select vendor"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          :cta="pastPaymentRequestsButton"
          @filter="sellerFilter.onFilter"
          @filter-abort="sellerFilter.onFilterAbort"
          @virtual-scroll="sellerFilter.onScroll"
        >
          <template #option="{ itemProps, opt }">
            <q-item v-bind="itemProps">
              <q-item-section>
                <q-item-label class="text-sm">
                  <span class="text-neutral">
                    {{ opt.shop_name }} {{ opt?.gstin ? `| ${opt.gstin}` : "" }}
                  </span>
                </q-item-label>
                <q-item-label v-if="opt.gstin || opt.state" caption lines="1">
                  <span class="text-neutral-70">
                    State: {{ opt.state }}
                    {{
                      opt?.tax_settings?.type === "tds"
                        ? `| TDS: ${opt.tax_settings.percentage}%`
                        : ""
                    }}
                  </span>
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </PSelectInput>
      </div>

      <div :class="bankDetailsClass">
        <p class="q-mb-xs">Bank Details</p>
        <p
          v-for="({ label, value }, index) in bankDetails"
          :key="index"
          class="q-mb-none"
        >
          <span class="text-weight-medium">{{ label }}: </span>
          <span>{{ value }}</span>
        </p>
      </div>

      <div
        v-if="!unref(formData)._id || !isPaymentsApprovalRoute"
        :class="bankDetailsClass + ' col-8'"
      >
        <div>
          <p class="flex q-mb-xs">
            <span>Portfolio Overview</span>
            <PBtn
              :loading="loadingPortfolio"
              dense
              flat
              fluid
              round
              class="q-ml-md"
              size="sm"
              @click="reloadPortfolio"
            >
              <PortfolioReloadIcon class="icon--md" />
              <PTooltip>Reload portfolio</PTooltip>
            </PBtn>
          </p>
        </div>
        <div class="row q-col-gutter-lg">
          <div class="col-6">
            <p class="text-weight-medium q-mb-xs">Outstanding Payables</p>
            <p>
              {{
                numberFormat({
                  number: userPortfolio.outstanding_payable_amount ?? 0,
                  style: "currency",
                })
              }}
            </p>
          </div>
          <div class="col-6">
            <p class="text-weight-medium q-mb-xs">
              <span> Unused Credits </span>
              <PTooltip>
                Sum of advances, excess payments minus(-) credits used for all
                payments & requests against.
              </PTooltip>
            </p>
            <p>
              {{
                numberFormat({
                  number: userPortfolio.unused_credits_payable_amount ?? 0,
                  style: "currency",
                })
              }}
            </p>
            <PCheckbox
              v-if="!isAdvanceRequest"
              v-model="formData.is_used_credits"
              disable
              label="Apply Credits"
              size="sm"
              @update:model-value="calculateUsedCredits"
            />
          </div>
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-lg" />
    <div v-if="!isAdvanceRequest" class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-xxl">
        <div class="col-4">
          <p>
            <span>POD: </span>
            <span class="text-weight-medium">
              {{ formData.invc_number || "-" }}
            </span>
          </p>
          <p>
            <span>POD Stamp Date: </span>
            <span class="text-weight-medium">
              {{ formatDate(formData?.pod_stamp_date, "D/M/YYYY") || "-" }}
            </span>
          </p>
        </div>
        <div class="col-4">
          <p>
            <span>Invoice Date: </span>
            <span class="text-weight-medium">
              {{ formatDate(formData?.invc_date, "D/M/YYYY") || "-" }}
            </span>
          </p>
        </div>
        <div class="col-4">
          <p>
            <span>PB : </span>
            <span class="text-weight-medium">
              {{ formData?.bill_number || "-" }}
            </span>
          </p>
          <p>
            <span>PB Date: </span>
            <span class="text-weight-medium">
              {{ formatDate(formData?.bill_date, "D/M/YYYY") || "-" }}
            </span>
          </p>
        </div>
      </div>
    </div>
    <div v-if="isAdvanceRequest">
      <div class="row q-col-gutter-xs">
        <div v-if="!isReleaseRequest" class="col-12">
          <PField
            v-if="!fetchingVendorAdvanceAgainstPBAllowed"
            v-model="formData.is_against_bill"
            :disable="Boolean(formState.view)"
            label="Type of Vendor Advance"
            required
          >
            <template #control>
              <POptionGroup
                v-model="formData.is_against_bill"
                :options="typeofVendorAdvanceOptions"
                @update:model-value="onUpdateAdvanceType"
              />
            </template>
          </PField>
        </div>
        <div v-if="isReleaseRequest" class="col-12">
          <PField
            v-model="formData.request_against"
            label="Type of Customer's Payment Received against"
            required
            :disable="formState.view"
          >
            <template #control>
              <POptionGroup
                v-model="formData.request_against"
                :disable="formState.view"
                :options="requestAgainstOptions"
                @update:model-value="onUpdateRequestAgainst"
              />
            </template>
          </PField>
        </div>
        <div v-if="formData.is_against_bill" :class="computedPBSelectorClasses">
          <PSelectInput
            v-model.trim="formData.bill_id"
            :cta="pastPaymentRequestsButton"
            :disable="Boolean(formState.view) || !formData.seller"
            :emit-value="false"
            :map-options="false"
            :options="unref(billFilter.entities)"
            :option-label="getBillOptionLabel"
            :options-loading="unref(billFilter.loading)"
            :rules="[validateBillForActivePr]"
            label="Select a Purchase Bill"
            option-value="_id"
            placeholder="Select a purchase bill"
            :help="computedPBHelpText"
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            required
            @filter="billFilter.onFilter"
            @filter-abort="billFilter.onFilterAbort"
            @virtual-scroll="billFilter.onScroll"
            @update:model-value="handlePurchaseBillUpdate"
          >
            <template #option="{ itemProps, opt }">
              <q-item v-bind="itemProps">
                <q-item-section>
                  <q-item-label class="text-sm">
                    <span class="text-neutral">
                      {{ opt.number || opt.internal_number }}
                    </span>
                  </q-item-label>
                  <q-item-label
                    v-if="opt.date || opt.total_amount"
                    caption
                    lines="1"
                  >
                    <span class="text-neutral-70">
                      {{
                        numberFormat({
                          number: opt.total_amount,
                          style: "currency",
                        })
                      }}
                      (inc. tax) Date: {{ formatDate(opt.date, "DD/MM/YYYY") }}
                    </span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
        </div>
        <div v-if="isReleaseRequest" class="col-12">
          <PSelectInput
            v-model.trim="formData.invc_id"
            :disable="Boolean(formState.view) || !formData.bill_id"
            :emit-value="false"
            :map-options="false"
            :options="unref(invoiceFilter.entities)"
            option-label="number"
            :options-loading="unref(invoiceFilter.loading)"
            label="Select an Invoice"
            :rules="[validateInvoiceAndBillForActivePr]"
            help="Against which payment is made"
            option-value="_id"
            placeholder="Select an invoice"
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            required
            @filter="invoiceFilter.onFilter"
            @filter-abort="invoiceFilter.onFilterAbort"
            @virtual-scroll="invoiceFilter.onScroll"
            @update:model-value="updateInvoiceHandler"
          >
            <template #option="{ itemProps, opt }">
              <q-item v-bind="itemProps">
                <q-item-section>
                  <q-item-label class="text-sm">
                    <span class="text-neutral">
                      {{ opt.number || opt.internal_number }}
                    </span>
                  </q-item-label>
                  <q-item-label
                    v-if="opt.date || opt.total_amount"
                    caption
                    lines="1"
                  >
                    <span class="text-neutral-70">
                      {{
                        numberFormat({
                          number: opt.total_amount,
                          style: "currency",
                        })
                      }}
                      (inc. tax) Date: {{ formatDate(opt.date, "DD/MM/YYYY") }}
                    </span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
        </div>

        <div v-if="isReleaseRequest" class="col-12">
          <PVDateInput
            v-model="formData.payment_received_date"
            :label="paymentReceivedDateLabel"
            placeholder="DD-MM-YYYY"
            required
            help="Customer paid date"
            :disable="
              Boolean(formState.view || formState.preview || !formData.invc_id)
            "
            :min-date="minPaymentReceivedDate"
          />
        </div>

        <div
          v-if="dealSettingsInfo && isReleaseRequest"
          class="col-12 flex items-center bg-info-20 q-py-sm q-px-xs rounded-borders q-mt-md"
        >
          <span
            class="no-wrap flex text-sm text-neutral text-weight-bold q-mr-xs"
          >
            <span :style="{ marginTop: '2px' }">
              <InfoIcon class="icon--sm q-mr-xs" />
            </span>
            <span>
              Note:
              <span class="text-weight-regular q-ml-xs">
                {{ dealSettingsInfo }}
              </span>
            </span>
          </span>
        </div>

        <div v-if="formData.is_against_bill && !isReleaseRequest" class="col-4">
          <div class="flex items-center">
            <div class="col">
              <PInput
                v-model.trim.number="computedPerCent"
                :disable="Boolean(formState.view) || !formData.seller"
                :validations="{
                  type: 'float',
                  gte: 5,
                  lte: 100,
                }"
                class="prepend-bg"
                label="&nbsp;"
                min="0"
                max="100"
                placeholder="e.g. 100"
                type="number"
                step="any"
                @keydown.space.prevent
                @update:model-value="performCalculations"
              >
                <template #prepend>
                  <span class="text-sm">%</span>
                </template>
              </PInput>
            </div>
            <div class="q-ml-lg q-mt-xxs">
              <PCheckbox
                v-model="formData.payment_has_tax"
                :disable="Boolean(formState.view) || !formData.seller"
                label="Tax?"
                @update:model-value="performCalculations"
              />
            </div>
          </div>
        </div>
        <div v-if="isAdvancePaymentAmountVisible" class="col-12">
          <PInput
            v-model.trim.number="computedTotalAmount"
            :disable="Boolean(formState.view) || formData.is_against_bill"
            :validations="{
              required: true,
              type: 'float',
              gt: 0,
            }"
            label="Advance Payment Amount"
            placeholder="0.00"
            type="number"
            step="any"
            @update:model-value="onUpdateAdvanceAmount"
          />
        </div>
      </div>
    </div>
    <q-separator
      v-if="!Boolean(formState.view) && isAdvanceRequest"
      class="q-mb-xxl q-mt-lg"
    />
    <div v-if="!isAdvanceRequest" class="overflow-hidden">
      <div class="flex justify-between items-center q-py-xs q-pr-xxs">
        <legend class="text-neutral text-weight-medium text-md">
          Delivered & Returned Items
        </legend>
        <div>
          <PBtn
            flat
            fluid
            text
            dense
            @click="() => viewAssociatedData('credit_notes')"
          >
            CNs ({{ formData?.credit_notes?.length ?? 0 }})
          </PBtn>
          <PBtn
            class="q-ml-xxs"
            flat
            fluid
            dense
            text
            @click="() => viewAssociatedData('debit_notes')"
          >
            DNs ({{ formData?.debit_notes?.length ?? 0 }})
          </PBtn>
          <PBtn
            class="q-ml-xxs"
            flat
            fluid
            text
            dense
            @click="() => viewAssociatedData('files')"
          >
            Files ({{ formData?.files?.length ?? 0 }})
          </PBtn>
        </div>
      </div>

      <q-separator v-if="formData?.lineitems?.length" />

      <PTable
        v-if="formData.lineitems.length"
        :pagination="pagination"
        rows-per-page-label=" "
        :rows-per-page-options="[]"
        :columns="productListColumns"
        :rows="formData.lineitems"
        :style="{ margin: '0 -15px', padding: 0 }"
        class="text-xs"
        dense
        flat
        wrap-cells
      >
        <template #header="headerProps">
          <q-tr :props="headerProps">
            <q-th
              v-for="col in headerProps.cols"
              :key="col.name"
              :props="headerProps"
              class="text-neutral-70 text-no-wrap"
              auto-width
            >
              {{ col.label }}
            </q-th>
          </q-tr>
        </template>
        <template #body="tableProps">
          <q-tr :props="tableProps">
            <q-td
              v-for="col in tableProps.cols"
              :key="col.name"
              :props="tableProps"
            >
              <ItemNameCell
                v-if="col.name === 'name'"
                :col="col"
                :table-props="tableProps"
              />

              <ItemQuantityCell
                v-else-if="['net_qty'].includes(col.name)"
                :col="col"
                :table-props="tableProps"
              />

              <ItemQuantityCell
                v-else-if="['invc_qty', 'returned'].includes(col.name)"
                :col="col"
                :table-props="tableProps"
                :unit="tableProps.row.po_unit"
              />

              <ItemRateCell
                v-else-if="col.name === 'rate'"
                :col="col"
                :table-props="tableProps"
              />

              <ItemAmountCell
                v-else-if="col.name === 'amount'"
                :col="col"
                :table-props="tableProps"
              />

              <div v-else-if="col.name === 'pb_rate'">
                <p class="q-mb-none text-no-wrap">
                  <PEditable
                    :model-value="col.value || 0"
                    :type="col.type"
                    disable
                  />
                  /
                  {{
                    useChangeCase(tableProps.row?.pb_unit ?? "", "capitalCase")
                      .value
                  }}
                </p>
                <p
                  class="q-mb-xs text-xxs text-neutral-70 text-italic text-nowrap"
                >
                  (exc. tax)
                </p>
              </div>

              <div
                v-else-if="
                  col.name === 'gst_exempted' && tableProps.row.gst_exempted
                "
              >
                <CheckMarkIcon class="icon--sm text-primary" />
              </div>

              <div v-else-if="col.name === 'is_po_unit_bigger'">
                <CheckMarkIcon v-if="col.value" class="icon--sm text-primary" />
                <CloseIcon v-else class="icon--sm text-primary" />
              </div>

              <PEditable
                v-else
                :model-value="col.value"
                :type="col.type"
                disable
              />
            </q-td>
          </q-tr>
        </template>
      </PTable>
      <span v-else class="text-neutral-50">No Items</span>
    </div>
    <q-separator
      v-if="!isAdvanceRequest && !isPaymentsApprovalRoute"
      class="q-mb-xxl q-mt-xxl"
    />
    <div class="row q-col-gutter-x-xxl">
      <div
        v-if="!isAdvanceRequest && !isPaymentsApprovalRoute"
        class="col-12 q-mb-xxl"
      >
        <PSelectInput
          model-value="100%"
          :options="['100%', '50%', '25%']"
          label="Do you want to pay certain percentage of total amount?"
          placeholder="Select percentage"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          disable
        >
        </PSelectInput>
      </div>
      <div
        v-if="unref(isRequestorSelectorVisible)"
        :class="'col-12 ' + getRequestorClass"
      >
        <PSelectInput
          v-model.trim="formData.requestor"
          :disable="Boolean(formState.view)"
          :options="unref(approversFilter.entities)"
          :options-loading="unref(approversFilter.loading)"
          help="On behalf of"
          label="Requestor"
          option-label="name"
          placeholder="Select requestor"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="51"
          @filter="approversFilter.onFilter"
          @filter-abort="approversFilter.onFilterAbort"
        >
          <template #option="{ itemProps, opt }">
            <q-item v-bind="itemProps" :disable="!opt.is_active">
              <q-item-section>
                <q-item-label class="text-sm">
                  <span class="text-neutral">{{ opt.name }}</span>
                </q-item-label>
                <q-item-label v-if="opt.email || opt.phone" caption lines="1">
                  <span class="text-neutral-70">
                    {{ opt.email }} {{ opt.email && opt.phone && "|" }}
                    {{ opt.phone }}
                  </span>
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </PSelectInput>
      </div>
    </div>
    <q-separator v-if="!isReleaseRequest" :class="getRequestorSeparatorClass" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-xxl">
        <div class="col-6">
          <PInput
            v-model.trim="formData.remarks"
            :disable="formState.view"
            :required="!formData.is_against_bill"
            label="Remarks"
            placeholder="Enter remarks"
            type="textarea"
            autogrow
          />
          <p
            v-if="
              unref(formData)?.pod?.status_cd === 'cancelled' &&
              unref(formData).status_cd === 'pending'
            "
            class="text-negative text-weight-bold q-mt-xs"
          >
            Note: POD against this payment request is cancelled. Please cancel
            this payment request.
          </p>
        </div>
        <div v-if="calculationsInfoVisible" class="col-6">
          <template
            v-for="(
              { hidden, label, caption, value, onClick }, index
            ) in calculations"
            :key="index"
          >
            <div v-if="!hidden" class="row q-col-gutter-lg q-mb-xs">
              <div class="col-6">
                <PBtn
                  v-if="label === 'Payments Made' && value > 0"
                  class="q-px-none"
                  :label="label"
                  dense
                  flat
                  text
                  @click="onClick"
                />
                <p
                  v-else
                  class="text-neutral q-mb-none"
                  :class="{
                    'q-mt-xs':
                      ['TDS', 'Applied Credits'].includes(label) &&
                      formData.status_cd === 'pending',
                  }"
                >
                  <TdsInfoTooltip v-if="label === 'TDS'" />

                  <span v-else>{{ label }}</span>
                </p>
                <span v-if="caption" class="text-xs text-neutral-70">
                  {{ caption }}
                </span>
              </div>
              <div class="col-6">
                <PInput
                  v-if="isEditableTds(label)"
                  v-model.number="computedTds"
                  :disable="formState.view"
                  :validations="{
                    type: 'float',
                    gte: 0,
                    lte: formData.total_amount,
                  }"
                  class="prepend-bg"
                  placeholder="0.00"
                  step="any"
                  type="number"
                  @update:model-value="onUpdateTds"
                >
                  <template #prepend>
                    <span class="text-sm">₹</span>
                  </template>
                </PInput>
                <div
                  v-else-if="
                    label === 'Applied Credits' &&
                    formData.status_cd === 'pending'
                  "
                  class="row items-center no-wrap q-gutter-x-sm"
                >
                  <PInput
                    v-model.number="computedAppliedCredits"
                    disable
                    :validations="{
                      type: 'float',
                      gte: 0,
                      lte: roundOff(formData.net_amount - absolutePaymentsMade),
                    }"
                    class="prepend-bg col"
                    placeholder="0.00"
                    step="any"
                    type="number"
                    :max="roundOff(formData.net_amount - absolutePaymentsMade)"
                    @update:model-value="onUpdateAppliedCredits"
                  >
                    <template #prepend>
                      <span class="text-sm">₹</span>
                    </template>
                  </PInput>
                  <PTooltip
                    anchor="bottom left"
                    self="top left"
                    :offset="[0, 4]"
                    class="text-sm"
                    max-width="500px"
                    >{{
                      numberFormat({
                        style: "currency",
                        number: computedAppliedCredits,
                      })
                    }}
                  </PTooltip>

                  <div class="flex items-center q-mb-xl q-mr-n-xxs">
                    <PBtn
                      color="primary"
                      flat
                      dense
                      round
                      @click="openAddCreditsModal"
                    >
                      <AddCreditsIcon class="icon--lg" />
                      <PTooltip class="text-xxs" max-width="500px">
                        Apply Credits
                      </PTooltip>
                    </PBtn>
                  </div>
                </div>
                <div
                  v-else
                  class="text-right"
                  :class="{
                    'text-error-70 cursor-pointer':
                      label === 'Payments Made' && value > 0,
                  }"
                >
                  {{ label === "Payments Made" && value > 0 ? "-" : "" }}
                  {{
                    numberFormat({
                      number: value,
                      style: "currency",
                    })
                  }}

                  <div
                    v-if="label === 'Applied Credits' && value > 0"
                    class="inline q-px-xxs"
                    style="margin-bottom: 25px"
                  >
                    <PBtn
                      color="primary"
                      flat
                      dense
                      round
                      @click="openAddCreditsModal"
                    >
                      <AddCreditsIcon class="icon" />
                      <PTooltip class="text-xxs" max-width="500px">
                        Apply Credits
                      </PTooltip>
                    </PBtn>
                  </div>
                </div>
              </div>
            </div>

            <ServiceItemList
              v-if="label === 'Applied Credits' && formData?.vas_items?.length"
              :vas-items="formData.vas_items"
              view-mode
            />
          </template>
        </div>
      </div>
    </div>
    <p
      v-if="disableSubmitButton && !dealSettings && !loadingPortfolio"
      class="text-negative text-weight-bold q-mt-sm"
    >
      Note: No Deal Settings Exist for Selected Vendor
      {{ unref(formData).seller?.shop_name }} and Customer
      {{ unref(formData).invc_id?.po_info?.buyer_name }}. Kindly contact
      Accounts team for more information
    </p>
    <template #footer-info>
      <p v-if="calculationsInfoVisible" class="q-mb-none">
        <span class="block text-neutral-80 text-xxs">Net Payable</span>
        <span class="text-sm">
          {{
            numberFormat({ number: formData?.net_payable, style: "currency" })
          }}
        </span>
      </p>
    </template>
    <template #extra-note>
      <p
        v-if="!isEmpty(computedExtraNote)"
        class="q-mr-xxl q-ml-xxl flex text-weight-medium justify-end text-neutral text-xs text-italic"
      >
        {{ computedExtraNote.text }}
      </p>
    </template>
  </PForm>
</template>

<script setup>
import { date, debounce, useQuasar } from "quasar";

import CheckMarkIcon from "assets/icons/checkmark.svg";
import PortfolioReloadIcon from "assets/icons/portfolio-reload.svg";
import CloseIcon from "assets/icons/close.svg";
import InfoIcon from "assets/icons/info.svg";
import AddCreditsIcon from "assets/icons/add_credits.svg";

import paymentRequestFormModel from "src/modules/payments/models/paymentRequestForm";

import { useChangeCase } from "@vueuse/integrations/useChangeCase";

import {
  numberFormat,
  decimalAdjust,
  roundOff,
  isEmpty,
} from "assets/scripts/utilities/helpers";

import { useAssociatedPaymentsData } from "../composables/associated-payments-data";
import { useCancel } from "../composables/cancel";
import { useInitiate } from "../composables/initiate";
import { useProcess } from "../composables/process";
import { useHold } from "../composables/hold";
import { useApprove } from "../composables/approve";
import { useReject } from "../composables/reject";

import * as paymentsApi from "src/api/payments";

import ApplyCreditsForm from "./ApplyCreditsDialogForm.vue";
import PTooltip from "src/components/PTooltip.vue";

const { removeQueryParams } = useRouterUtilities();

const { formatDate } = date;

const pForm = ref(null);

const route = useRoute();

const props = defineProps({
  stage: {
    type: String,
    default: "",
  },
  requestAgainst: {
    type: String,
    default: "",
  },
});

const pagination = {
  rowsPerPage: 5,
};

const isPaymentsApprovalRoute = computed(() => {
  return route.name === "purchases-payments";
});

const minPaymentReceivedDate = ref(null);

const updateInvoiceHandler = (invoice) => {
  if (!unref(invoice).date) {
    return;
  }

  const invoiceDate = new Date(unref(invoice).date);

  minPaymentReceivedDate.value = new Date(invoiceDate);
};
const bankDetailsClass = computed(() => {
  let className = "col-4";

  if (unref(isPaymentsApprovalRoute)) {
    className += " " + "q-pt-none q-mt-none";
  }

  if (unref(isAdvanceRequest)) {
    className += " " + "q-pt-xxl q-mt-xs";
  } else {
    className += " " + " q-pt-none";
  }

  return className;
});

const getRequestorSeparatorClass = computed(() => {
  if (!unref(isAdvanceRequest) && !unref(isPaymentsApprovalRoute)) {
    return "q-mb-xl q-mt-xl";
  }

  return "q-mb-xxl q-mt-sm";
});

const getRequestorClass = computed(() => {
  if (unref(isAdvanceRequest)) {
    return "q-mt-xs";
  }

  return "";
});

const { formState, paymentRequest } = storeToRefs(usePaymentRequestStore());
const {
  postPaymentRequest,
  putPaymentRequest,
  togglePaymentRequestForm,
  togglePastPaymentRequestsView,
} = usePaymentRequestStore();

const initialFormData = computed(() => {
  const requestAgainstDefaultValue = {
    RELEASE: "CAP",
    ADVANCE: null,
    "": "pod",
  };

  return {
    is_advance: !unref(formState).edit,
    request_against: requestAgainstDefaultValue[props.requestAgainst],
  };
});

const { formData, isLoading, closeForm, resetForm, submitForm, validateForm } =
  useForm({
    formModel: paymentRequestFormModel(initialFormData.value),
    entity: paymentRequest,
    formRef: pForm,
    isForm: formState,
    post: postPaymentRequest,
    put: putPaymentRequest,
    toggleForm: togglePaymentRequestForm,
  });

const isReleaseRequest = computed(() => {
  if (!unref(formData)._id) {
    return props.requestAgainst === "RELEASE";
  }

  return ["CAP", "CFP", "CGR"].includes(unref(formData).request_against);
});

const isAdvanceRequest = computed(() => unref(formData).is_advance);

const { isRightDrawerOpen } = storeToRefs(useAppStore());

watchEffect(() => {
  if (
    !unref(isReleaseRequest) &&
    !unref(formData)._id &&
    unref(isRightDrawerOpen)
  ) {
    unref(formData).is_against_bill = false;
  }
});

const calculationsInfoVisible = computed(() => {
  if (!unref(isReleaseRequest)) {
    return true;
  }

  if (unref(formData)._id) {
    return true;
  }

  return false;
});

const computedPBSelectorClasses = computed(() => {
  if (unref(isReleaseRequest)) {
    return "col-12";
  }

  if (unref(formState).edit) {
    if (unref(formData).request_against) {
      return "col-12";
    }
  }

  return "col-8";
});

const computedPBHelpText = computed(() => {
  if (unref(isReleaseRequest)) {
    return "Against which payment to be released";
  }

  return "";
});

const isRequestorSelectorVisible = computed(() => {
  return (
    !(unref(formState).edit && unref(isPaymentsApprovalRoute)) &&
    !unref(isReleaseRequest)
  );
});

const isAdvancePaymentAmountVisible = computed(() => {
  return (
    (!unref(formState).view || !isPaymentsApprovalRoute.value) &&
    unref(isAdvanceRequest) &&
    !unref(isReleaseRequest)
  );
});

const formHeading = computed(() => {
  const requestAgainstToFormHeadingMap = {
    ADVANCE: "Advance Payment",
    RELEASE: "Release Payment",
  };

  if (unref(isAdvanceRequest) && !unref(formState).edit) {
    return requestAgainstToFormHeadingMap[props.requestAgainst];
  }

  return `Payment Request: ${unref(formData).internal_number}`;
});

const disableVendorSelector = computed(() => {
  return unref(formState).view || !unref(isAdvanceRequest);
});

const disableApproverSelector = computed(() => {
  return unref(formState).view;
});

const { viewAssociatedData } = useAssociatedPaymentsData({ formData });

let approversFilter = {};

approversFilter = useFilter({
  entity: "user",
  options: {
    params: {
      roles: "kam",
      isActive: 1,
    },
  },
});

let sellerFilter = {};

sellerFilter = useFilter({
  entity: "user",
  options: {
    params: {
      roles: "seller",
      isActive: 1,
    },
  },
});

const getSellerOptionLabel = (opt) => {
  const { shop_name, gstin } = opt;

  return `${shop_name} ${gstin ? "| " + gstin : ""}`;
};

const { getUsers } = useUserStore();

const getRachitKDogra = async () => {
  try {
    const response = await getUsers({
      params: {
        search: "Rachit K Dogra",
      },
    });

    return response?.data?.data?.data[0];
  } catch (error) {
    console.error(error);
  }
};

const populateApprovers = debounce(async () => {
  let approvers = [await getRachitKDogra()];

  let accountOwner = unref(formData)?.seller?.account_owner;

  approvers.push(accountOwner);

  unref(formData).approvers = approvers.filter((approver, index, approvers) => {
    return (
      approver && approvers.findIndex((a) => a?._id === approver?._id) === index
    );
  });
}, 100);

const bankDetails = computed(() => {
  const { seller_info } = unref(formData);

  return [
    {
      value: seller_info?.bank_name,
      label: "Name",
    },
    {
      value: seller_info?.bank_beneficiary_name,
      label: "Beneficiary",
    },
    {
      value: seller_info?.bank_account_number,
      label: "A/C",
    },
    {
      value: seller_info?.bank_ifsc,
      label: "IFSC",
    },
  ];
});

const { getUserPortfolio } = useUserStore();

const userPortfolio = ref({});

const loadingPortfolio = ref(false);

const viewPastPaymentRequests = async () => {
  try {
    togglePastPaymentRequestsView({
      edit: false,
      level: 0,
      open: true,
      view: true,
    });
  } catch (error) {
    console.error(error);
  }
};

const reloadPortfolio = async () => {
  try {
    loadingPortfolio.value = true;

    const seller_id = unref(formData)?.seller?._id || unref(formData)?.seller;

    if (seller_id) {
      if (unref(isAdvanceRequest) || unref(formData).status_cd === "pending") {
        userPortfolio.value = await getUserPortfolio(seller_id);

        updateFinancialSummary();
      } else {
        userPortfolio.value = {
          outstanding_payable_amount:
            unref(formData)?.financial_summary?.outstanding_payables || 0,

          unused_credits_payable_amount:
            unref(formData)?.financial_summary?.unused_credits || 0,
        };
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    loadingPortfolio.value = false;
  }
};

let billFilter = {};

let invoiceFilter = {};

const dateBeforeNDays = computed(() => {
  let days = 120;

  return date.subtractFromDate(new Date(), { days });
});

const invoiceFilterStages = computed(() => {
  const stages = "qc_accepted,pod_accepted";

  if (unref(formData).request_against === "CFP") {
    return stages;
  }

  return stages + ",qc_received,pod_pending,pod_received,pod_rejected";
});

const initInvoiceFilter = () => {
  invoiceFilter = useFilter({
    entity: "invoice",
    options: {
      params: {
        status_cd: "active",
        bill_id: unref(formData).bill_id?._id,
        stages: unref(invoiceFilterStages),
      },
    },
  });
};

const initBillFilter = () => {
  billFilter = useFilter({
    entity: "purchaseBill",
    options: {
      params: {
        status_cd: "active",
        startDate: unref(dateBeforeNDays),
        endDate: new Date(),
        sellers: unref(formData)?.seller?._id,
      },
    },
  });
};

const getBillOptionLabel = (opt) => {
  const { number, internal_number } = opt;

  return number || internal_number;
};

const getPayments = async ({ params }) => {
  try {
    const response = await paymentsApi.indexAccounts({
      params,
    });

    return response?.data?.data?.data;
  } catch (error) {
    console.error(error);
  }
};

const validateBillForActivePr = async (purchaseBill) => {
  if (unref(isReleaseRequest)) {
    return true;
  }

  if (purchaseBill) {
    const activePrs = getPayments({
      params: {
        bill_id: purchaseBill._id,
        status_cd:
          "pending,awaiting_approval,approved,initiated,processed,hold",
      },
    });

    if (activePrs?.length) {
      return `This is already an active payment request ${
        activePrs[0]?.internal_number
      } of amount ${numberFormat({
        number: activePrs[0]?.net_payable,
        style: "currency",
      })}. Please contact support for more information.`;
    } else {
      return true;
    }
  }
};

const validateInvoiceAndBillForActivePr = async (invoice) => {
  doesDealSettingsExists.value = true;

  if (!invoice || !unref(formData).bill_id) {
    return;
  }

  const params = {
    bill_id: unref(formData).bill_id?._id,
    invc_id: invoice._id,
    request_against: unref(formData).request_against,
    status_cd: "pending,awaiting_approval,approved,initiated,processed,hold",
  };

  const { deal_setting = {} } = unref(invoice);

  const activePrs = await getPayments({ params });

  if (isEmpty(deal_setting)) {
    doesDealSettingsExists.value = false;
  } else {
    dealSettings.value = deal_setting;
  }

  if (activePrs?.length) {
    return `There is already an active payment request ${
      activePrs[0]?.internal_number
    } of amount ${numberFormat({
      number: activePrs[0]?.net_payable,
      style: "currency",
    })} against  Purchase Bill ${unref(formData).bill_id?.number} and Invoice ${invoice.number}. Please contact support for more information.`;
  }

  return true;
};

const resetAdvanceRequestData = () => {
  unref(formData).bill_id = null;

  unref(formData).invc_id = null;

  unref(formData).percentage_pay = 100;

  unref(formData).payment_has_tax = true;

  unref(formData).total_amount = 0;

  unref(formData).total_tax = 0;

  if (unref(isReleaseRequest)) {
    unref(formData).is_against_bill = true;
  } else {
    unref(formData).is_against_bill = false;
  }

  unref(formData).total_amount = 0;

  unref(formData).net_amount = 0;

  unref(formData).net_payable = 0;

  unref(formData).requestor = null;

  unref(formData).remarks = "";
};

const populateVendorData = async (vendor) => {
  if (vendor) {
    const {
      bank_account_number,
      bank_ifsc,
      bank_name,
      bank_beneficiary_name,
      tax_settings,
    } = vendor;

    unref(formData).seller_info = {
      bank_account_number,
      bank_ifsc,
      bank_name,
      bank_beneficiary_name: bank_beneficiary_name,
      ...unref(formData).seller_info,
    };

    unref(formData).tds = {
      ...unref(formData).tds,
      code: tax_settings?.code ?? "",
      percentage: tax_settings?.percentage ?? 0,
    };

    if (!unref(formState).edit && unref(formData).is_advance) {
      resetAdvanceRequestData();
    }
  }

  nextTick(() => {
    if ([undefined, "pending"].includes(unref(paymentRequest)?.status_cd)) {
      populateApprovers();
    }
  });

  await reloadPortfolio();

  initBillFilter();
};

const pastPaymentRequestsButton = computed(() => {
  if (!unref(formData)._id) {
    return {};
  }

  return {
    dense: true,
    flat: true,
    fluid: true,
    text: true,
    label: "Past Payments",
    onClick: viewPastPaymentRequests,
  };
});

const productListColumns = computed(() => {
  let columns = [
    {
      align: "left",
      name: "name",
      label: "Product Name",
      field: "name",
    },
    {
      align: "left",
      name: "net_qty",
      label: "Net Qty (PB)",
      field: "qty",
    },
    {
      align: "left",
      name: "invc_qty",
      label: "Invoice Qty",
      field: "invc_qty",
    },
    {
      align: "left",
      name: "returned",
      label: "Returned",
      field: "cn_qty",
    },

    {
      align: "right",
      name: "pb_rate",
      label: "PB Rate",
      field: "pb_rate",
      type: "currency",
    },
    {
      align: "left",
      name: "pb_gst",
      label: "PB GST",
      field: "pb_gst",
      format: (val) => (val ? `${val}%` : "-"),
    },
    {
      align: "left",
      name: "conversion_factor",
      label: "Conv. Factor",
      field: "conversion_factor",
    },
    {
      align: "center",
      name: "is_po_unit_bigger",
      label: "PO Unit Big?",
      field: "is_po_unit_bigger",
    },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
      type: "currency",
    },
  ];

  return columns;
});

const onUpdateAdvanceType = () => {
  if (!unref(formData).is_against_bill) {
    unref(formData).bill_id = null;

    unref(formData).percentage_pay = 100;

    unref(formData).payment_has_tax = true;

    unref(formData).total_amount = 0;

    unref(formData).total_tax = 0;
  }

  performCalculations();
};

const computedPerCent = computed({
  get: () => roundOff(unref(formData).percentage_pay),
  set: (value) => {
    unref(formData).percentage_pay = value;
  },
});

const computedTotalAmount = computed({
  get: () => roundOff(unref(formData).total_amount),
  set: (value) => {
    unref(formData).total_amount = value;
  },
});

const absolutePaymentsMade = computed(() => {
  return Math.abs(unref(formData).payments_made);
});

const calculateNetPayable = () => {
  const netPayable = Math.max(
    unref(formData).net_amount -
      unref(formData).used_credits -
      (unref(formData).adjusted_credits || 0) -
      unref(absolutePaymentsMade),
    0,
  );

  unref(formData).net_payable = decimalAdjust("round", netPayable, 0);

  const roundingDiff = roundOff(unref(formData).net_payable - netPayable);

  unref(formData).adjustments = unref(formData).adjusted_credits
    ? unref(formData).adjusted_credits - roundingDiff
    : roundingDiff;
};

const updateFinancialSummary = () => {
  const outstanding_payables = unref(userPortfolio).outstanding_payable_amount;
  const unused_credits =
    unref(userPortfolio).unused_credits_payable_amount -
    unref(formData).used_credits;

  unref(formData).financial_summary = {
    outstanding_payables,
    unused_credits,
    balance: outstanding_payables - unused_credits,
  };

  calculateNetPayable();
};

const resetAppliedCredits = () => {
  unref(formData).is_used_credits = false;

  unref(formData).used_credits = 0;

  unref(formData).applied_credits_lineitems = [];
};
const calculateUsedCredits = () => {
  resetAppliedCredits();

  updateFinancialSummary();
};

const calculateNetAmount = () => {
  unref(formData).net_amount =
    unref(formData).total_amount +
    unref(formData).total_tax -
    unref(formData).tds?.amount;

  calculateUsedCredits();
};

const calculateTdsAmount = () => {
  unref(formData).tds.amount =
    unref(formData).total_amount * (unref(formData).tds.percentage / 100);

  calculateNetAmount();
};

const resetInvoiceFields = () => {
  formData.value.invc_id = null;

  formData.value.invc_number = null;

  formData.value.invc_date = null;
};

const resetPurchaseBillFields = () => {
  formData.value.bill_id = null;

  formData.value.bill_number = null;

  formData.value.bill_date = null;
};

const handlePurchaseBillUpdate = () => {
  if (unref(isReleaseRequest)) {
    resetInvoiceFields();

    initInvoiceFilter();

    return;
  }

  performCalculations();
};

const performCalculations = () => {
  if (unref(formData).is_against_bill) {
    const { bill_id, percentage_pay, payment_has_tax } = unref(formData);

    if (bill_id) {
      const { total_amount, total_tax } = bill_id;

      const perCentTotalAmount = (total_amount * percentage_pay) / 100;
      const perCentTotalTax = (total_tax * percentage_pay) / 100;

      unref(formData).total_amount = perCentTotalAmount - perCentTotalTax;

      unref(formData).total_tax = payment_has_tax ? perCentTotalTax : 0;
    }
  }

  calculateTdsAmount();
};

const onUpdateAdvanceAmount = () => {
  const { bill_id } = unref(formData);

  if (bill_id) {
    const { total_amount, total_tax } = bill_id;

    let totalAmountWithoutTax = total_amount - total_tax;

    unref(formData).percentage_pay =
      (unref(formData).total_amount / totalAmountWithoutTax) * 100;
  }

  performCalculations();
};

const calculations = computed(() => {
  return [
    {
      label: "Total Amount",
      caption: "Excludes Tax",
      value: unref(formData).total_amount,
    },
    {
      label: "Total Tax",
      value: unref(formData).total_tax,
    },
    {
      label: "TDS",
      value: unref(formData)?.tds?.amount ?? 0,
    },
    {
      hidden: unref(formState).edit && unref(isPaymentsApprovalRoute),
      label: "Net Amount",
      value: unref(formData).net_amount,
    },
    {
      hidden: unref(isAdvanceRequest) || unref(isPaymentsApprovalRoute),
      label: "Payments Made",
      value: unref(absolutePaymentsMade) ?? 0,
      onClick: () => {
        unref(formData).payments_made &&
          viewAssociatedData("associated_payments");
      },
    },
    {
      hidden: unref(isAdvanceRequest),
      label: "Applied Credits",
      value: unref(formData).used_credits,
    },
    {
      label: "Round Off",
      value: unref(formData).adjustments,
    },
    {
      label: "Net Payable",
      value: unref(formData)?.net_payable,
    },
  ];
});

const computedTds = computed({
  get: () => roundOff(unref(formData)?.tds?.amount),
  set: (value) => {
    unref(formData).tds.amount = value;
  },
});

const isEditableTds = (label) => {
  return (
    label === "TDS" &&
    unref(formData)?.seller?.tax_settings?._id &&
    unref(formData).status_cd === "pending"
  );
};

const onUpdateTds = (tds) => {
  unref(formData).tds = {
    ...unref(formData).tds,
    amount: tds,
  };

  calculateNetAmount();
};

const computedAppliedCredits = computed({
  get: () => roundOff(unref(formData)?.used_credits),
  set: (value) => {
    unref(formData).used_credits = value;
  },
});

const onUpdateAppliedCredits = (usedCredits) => {
  unref(formData).used_credits = usedCredits;

  updateFinancialSummary();
};

watch(
  [() => unref(formState).open, () => unref(formState).edit],
  async ([isOpen, isEdit]) => {
    await nextTick();

    if (isOpen && isEdit) {
      await populateVendorData(unref(formData)?.seller);

      if (unref(formData).status_cd === "pending") {
        calculateTdsAmount();
      }
    }

    if (isOpen && !isEdit) {
      unref(formData).is_advance = true;
    }
  },
  {
    immediate: true,
  },
);

const doesDealSettingsExists = ref(true);
const dealSettings = ref(null);

const paymentToBeReleased = computed(() => {
  if (!unref(dealSettings)) {
    return {
      percentage: -1,
    };
  }

  let paymentPercentage =
    unref(dealSettings)?.payment_distribution?.payment_received;

  if (unref(formData).request_against === "CGR") {
    paymentPercentage = unref(dealSettings)?.payment_distribution?.gst_hold;
  }

  if (unref(formData).request_against === "CFP") {
    paymentPercentage = unref(dealSettings)?.payment_distribution?.margin_money;
  }

  return {
    percentage: paymentPercentage,
  };
});

const disableSubmitButton = computed(() => {
  if (unref(loadingPortfolio)) {
    return true;
  }

  if (!unref(isReleaseRequest) || !unref(formData).invc_id) {
    return false;
  }

  if (unref(paymentToBeReleased).percentage === 0) {
    return true;
  }

  return Boolean(!unref(doesDealSettingsExists));
});

const { sendForApprovalButton } = useSendForApproval({
  formData,
  closeForm,
  validateForm,
  disableSubmitButton,
});

const { initiateButton } = useInitiate({ formData, closeForm });
const { cancelButton } = useCancel({ formData, closeForm });
const { processButton } = useProcess({ formData });
const { holdButton } = useHold({ formData, closeForm });
const { approveButton } = useApprove({ formData, closeForm });
const { rejectButton } = useReject({ formData, closeForm });
const { cancelProcessingButton } = useCancelProcessing({ formData, closeForm });

const totalNetPayable = computed(() => {
  return unref(formData).net_payable;
});

const labelForOneClickProcessForm = computed(() => {
  return `${unref(formData).seller?.shop_name} | ${unref(formData).seller_info?.bank_name} ${" "}, ${unref(formData).seller_info?.bank_account_number} with Transfer Amount: ${numberFormat({ number: unref(totalNetPayable), style: "currency" })}`;
});

const { oneClickProcessButton, computedOneClickProcess } = useOneClickProcess({
  closeForm,
  totalNetPayable,
  isLoading,
  label: "1-Click Process Payment",
  selectedPR: formData,
  componentProps: {
    title: "1-Click Process Payment",
    label: labelForOneClickProcessForm,
    labelClass: "text-md text-weight-medium text-neutral-100",
  },
});

const submitButton = computed(() => {
  const emptyBtn = {};

  if (
    unref(formData)?.pod?.status_cd === "cancelled" &&
    unref(formData).status_cd === "pending"
  ) {
    return emptyBtn;
  }

  if (
    unref(formData).status_cd === "pending" ||
    (unref(isAdvanceRequest) && !unref(formState).edit)
  ) {
    return unref(sendForApprovalButton);
  }

  if (unref(isPaymentsApprovalRoute)) {
    // if (unref(formData).status_cd === "awaiti")
    // current tab
    if (props.stage === "awaiting_approval") {
      return unref(approveButton);
    }

    return emptyBtn;
  }

  if (unref(formData).status_cd === "approved") {
    return unref(initiateButton);
  }

  if (["initiated", "hold"].includes(unref(formData).status_cd)) {
    return unref(oneClickProcessButton);
  }

  if (unref(formState).view) {
    return emptyBtn;
  }

  return {
    loading: unref(isLoading),
  };
});

const secondaryCta = computed(() => {
  const emptyBtn = {};

  if (unref(isPaymentsApprovalRoute)) {
    return emptyBtn;
  }

  if (
    ["initiated", "hold", "transfer_failed"].includes(unref(formData).status_cd)
  ) {
    return unref(processButton);
  }

  return emptyBtn;
});

const quaternaryCta = computed(() => {
  if (unref(isPaymentsApprovalRoute)) {
    if (props.stage === "awaiting_approval") {
      return unref(rejectButton);
    }

    return {};
  }

  if (["processing"].includes(unref(formData).status_cd)) {
    return unref(cancelProcessingButton);
  }

  if (
    ["pending", "awaiting_approval", "approved", "rejected"].includes(
      unref(formData).status_cd,
    )
  ) {
    return unref(cancelButton);
  }

  if (["initiated"].includes(unref(formData).status_cd)) {
    return unref(holdButton);
  }

  return {};
});

const isTypeOfPaymentSelectorVisible = computed(() => {
  if (unref(isReleaseRequest)) {
    return false;
  }

  return unref(formState).edit || !unref(isAdvanceRequest);
});

const dealSettingsInfo = computed(() => {
  if (
    unref(formData).invc_id &&
    (!unref(disableSubmitButton) || unref(paymentToBeReleased).percentage >= -1)
  ) {
    const supplier = unref(formData).seller?.shop_name;

    let note = `As per the deal setting between ${unref(formData).invc_id?.po_info?.buyer_name} and ${supplier},  `;

    if (unref(formData).request_against === "CGR") {
      note += ` a GST hold of  ${unref(paymentToBeReleased).percentage}% will be released to ${supplier}. No adjustments of cn/dn will be applied to this payment release`;

      return note;
    }

    return (
      note +
      ` ${unref(paymentToBeReleased).percentage}% of the payment will be released to ${supplier}. The final amount will be calculated after adjusting for credit/debit notes and any delays.`
    );
  }

  return "";
});

const onUpdateRequestAgainst = () => {
  initInvoiceFilter();

  resetPurchaseBillFields();

  resetInvoiceFields();

  formData.value.payment_received_date = null;
};

const enableVendorAdvanceAgainstPB = ref(false);

const fetchingVendorAdvanceAgainstPBAllowed = ref(false);

const fetchVendorAdvanceAgainstPBAllowedStatus = async () => {
  try {
    fetchingVendorAdvanceAgainstPBAllowed.value = true;

    const response = await paymentsApi.vendorAdvanceAgainstPBAllowed();

    enableVendorAdvanceAgainstPB.value = Boolean(response?.data?.data?.allowed);

    fetchingVendorAdvanceAgainstPBAllowed.value = false;
  } catch (error) {
    fetchingVendorAdvanceAgainstPBAllowed.value = false;

    console.error(error);
  }
};

watchEffect(async () => {
  if (
    unref(isPaymentsApprovalRoute) &&
    !unref(isReleaseRequest) &&
    unref(isRightDrawerOpen)
  ) {
    await fetchVendorAdvanceAgainstPBAllowedStatus();
  }
});

const typeofVendorAdvanceOptions = computed(() => {
  const options = [
    {
      label: "Lump Sum Amount",
      value: false,
    },
    {
      label: "Against a Purchase Bill",
      value: true,
      disable: !unref(enableVendorAdvanceAgainstPB),
    },
  ];

  return options;
});

const computedExtraNote = computed(() => {
  if (
    unref(formData).status_cd === "processing" &&
    unref(formData).transfer_scheduled_at
  ) {
    const transferScheduledAt = formatDate(
      unref(formData).transfer_scheduled_at,
      "DD MMM YYYY HH:mm:SS A",
    );

    return {
      text: `Scheduled At ${transferScheduledAt} `,
    };
  }

  if (
    unref(formData).status_cd === "initiated" &&
    !unref(computedOneClickProcess).allowed
  ) {
    return {
      text: `${unref(computedOneClickProcess).extraNote}`,
    };
  }

  return {};
});

const onClose = () => {
  removeQueryParams();

  closeForm();
};

const requestAgainstOptions = computed(() => {
  return [
    {
      label: "Customer paid advance ",
      value: "CAP",
      description: "Advance paid to us",
      labelClass: "q-mb-none text-weight-medium q-mr-xxl",
      descriptionClass: "q-mb-none text-neutral-70 text-xs q-mr-xxl",
    },
    {
      label: "Customer fully paid us",
      value: "CFP",
      description: "Margin money holdback release",
    },
    {
      label: "Customer Paid GST",
      value: "CGR",
      description: "GST holdback release",
    },
  ];
});

const paymentReceivedDateLabel = computed(() => {
  if (unref(formData).request_against === "CGR") {
    return "Customer GST Release Date";
  }

  return "Amount Received On";
});

const $q = useQuasar();

const openAddCreditsModal = () => {
  $q.dialog({
    component: ApplyCreditsForm,
    componentProps: {
      lineitems: JSON.parse(
        JSON.stringify(toRaw(unref(formData).applied_credits_lineitems)),
      ),
      netAmount: unref(formData).net_amount - unref(absolutePaymentsMade),
      adjustedCredits: unref(formData).adjusted_credits,
    },
  }).onOk((payload) => {
    unref(formData).is_used_credits = true;

    unref(formData).adjusted_credits = payload?.adjusted_credits;

    unref(formData).applied_credits_lineitems =
      payload?.applied_credits_lineitems.map((credits) => {
        return {
          ...credits,
          bill: {
            _id: unref(formData).bill_id?._id,
            number: unref(formData).bill_number,
          },
        };
      });

    onUpdateAppliedCredits(payload?.totalAppliedCredits);
  });
};
</script>
